import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { saljaHusbilFaq, saljaHusbilSections } from "@/data/nationella-guider-3";
import {
  saljaHusbilExtraFaq,
  saljaHusbilExtraSections,
  saljaHusbilTldr,
  GUIDE_AUTHOR,
  GUIDE_UPDATED,
} from "@/data/nationella-guider-3-extra";
import hero from "@/assets/guider/salja-husbil.jpg";

const URL = "https://www.akhusbilfritid.se/salja-husbil";

const sections = [...saljaHusbilSections, ...saljaHusbilExtraSections];
const faq = [...saljaHusbilFaq, ...saljaHusbilExtraFaq];

export const Route = createFileRoute("/salja-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(faq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Sälja husbil 2026 – förmedling, inbyte eller privat?" },
      {
        name: "description",
        content:
          "Vad förmedling, inbyte och privatförsäljning ger i handen, hur du värderar rätt, annonsen som säljer och varför husbilen ibland inte går att sälja.",
      },
      {
        name: "keywords",
        content:
          "sälja husbil, husbilsförmedling, värdera husbil online, sälja husvagn, svårt sälja husbil, byta husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Sälja husbil – förmedling, inbyte eller privat?" },
      {
        property: "og:description",
        content: "Nettot efter avgifter, värderingsmetoden vi själva använder och papperen som gör affären trygg.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Sälja husbil – förmedling, inbyte eller privat?",
          url: URL,
          inLanguage: "sv-SE",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Sälja husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const facts = [
  { k: "10–20 %", v: "under marknadspris vid inbyte — men klart samma dag" },
  { k: "5–9 %", v: "typisk förmedlingsavgift, du behåller ägandet" },
  { k: "mars–maj", v: "starkaste säljfönstret, näst bäst augusti" },
  { k: "1,5–2,5 tkr", v: "ett fukttest som kortar säljtiden mer än en prissänkning" },
];

function Page() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Sälja"
        title={
          <>
            Sälja husbil — <span className="font-italic-display text-accent">nettot</span> avgör, inte annonspriset.
          </>
        }
        sub="Förmedling, inbyte eller privat: vad varje väg faktiskt ger i handen, hur du värderar rätt, och vad du gör när bilen inte säljs."
      />

      <section className="container-edge pt-10 md:pt-14">
        <figure className="overflow-hidden rounded-3xl border border-border">
          <img
            src={hero}
            alt="Handslag vid en vit husbil efter avslutad husbilsaffär"
            width={1600}
            height={912}
            className="h-auto w-full object-cover"
            decoding="async"
          />
        </figure>
      </section>

      <section className="container-edge pt-10 md:pt-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Guiden"
          sections={sections}
          faq={faq}
          tldr={saljaHusbilTldr}
          updated={GUIDE_UPDATED}
          author={GUIDE_AUTHOR}
        />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/vardera-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Värdera din husbil — verktyg med öppen räkning</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — gränsvärden och vad skador kostar</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/kontakt"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Gratis värdering — ring oss i Kalix</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
